/**
 * Add Last-Minute Trip Dialog - Phase 7
 * Preview-only dialog for checking if new trip can fit
 * Shows three-tier result: zero-disruption / local-repair / failed
 */

import LocationOffIcon from "@mui/icons-material/LocationOff";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addHours } from "date-fns";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PlacementWithLocation,
  Ramp,
  Stack as StackType,
  Tenant,
} from "../../../types";
import { findSpotForPosition } from "../TemporalTest/positionMapping";
import {
  LastMinuteInsertionRampResult,
  runLastMinuteInsertionOnRamp,
} from "../TemporalTest/temporalRampIntegration";

export interface AddLastMinuteTripDialogProps {
  open: boolean;
  onClose: () => void;
  ramp: Ramp;
  stack: StackType;
  placements: PlacementWithLocation[];
  currentTime?: Date;
}

export function AddLastMinuteTripDialog({
  open,
  onClose,
  ramp,
  stack,
  placements,
  currentTime,
}: AddLastMinuteTripDialogProps) {
  const { t } = useTranslation();
  const defaultStart = currentTime || new Date();
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [startTime, setStartTime] = useState<Date>(defaultStart);
  const [endTime, setEndTime] = useState<Date>(addHours(defaultStart, 4));
  const [maxDisruption, setMaxDisruption] = useState(2);
  const [previewResult, setPreviewResult] =
    useState<LastMinuteInsertionRampResult | null>(null);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedTenant(null);
      setStartTime(currentTime || new Date());
      setEndTime(addHours(currentTime || new Date(), 4));
      setMaxDisruption(2);
      setPreviewResult(null);
    }
  }, [open, currentTime]);

  // Get tenant options from stack (both placed and unplaced)
  const tenantOptions = React.useMemo(() => {
    return stack.tenants.filter((t) => t.aircraft != null);
  }, [stack.tenants]);

  const handlePreview = () => {
    if (!selectedTenant || !selectedTenant.aircraft) return;

    // Validate time range
    if (startTime >= endTime) {
      alert(t('screens.ramp.lastMinuteInsertion.dialog.errors.invalidTimeRange'));
      return;
    }

    try {
      const result = runLastMinuteInsertionOnRamp(
        ramp,
        stack,
        {
          aircraft: selectedTenant.aircraft,
          startTime,
          endTime,
          trip_id: selectedTenant.id, // Ensure placement key matches lookup key
        },
        {
          maxDisruption,
          startTime: currentTime || new Date(),
          horizonHours: 24,
        }
      );

      setPreviewResult(result);
    } catch (error) {
      console.error("[AddLastMinuteTrip] Preview error:", error);
      alert(
        `Preview failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  // Helper: Get tail number from aircraft ID
  const getTailNumber = (aircraftId: string): string => {
    const placement = placements.find((p) => p.id === aircraftId);
    return placement?.tail_number || aircraftId;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('screens.ramp.lastMinuteInsertion.dialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Step 1: Select Aircraft */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('screens.ramp.lastMinuteInsertion.dialog.labels.aircraft')}
              </Typography>
              <Autocomplete
                value={selectedTenant}
                onChange={(_, value) => setSelectedTenant(value)}
                options={tenantOptions}
                getOptionLabel={(tenant) =>
                  `${tenant.tail_number} - ${
                    tenant.aircraft?.model || t('screens.ramp.lastMinuteInsertion.dialog.aircraftStatus.unknown')
                  }`
                }
                renderOption={(props, tenant) => {
                  // Calculate status info for this tenant
                  let statusLabel = t('screens.ramp.lastMinuteInsertion.dialog.aircraftStatus.unplaced');
                  let statusColor: "default" | "info" | undefined = undefined;
                  let statusVariant: "filled" | "outlined" = "outlined";
                  let statusIcon = <LocationOffIcon />;

                  if (
                    tenant.position.x !== null &&
                    tenant.position.y !== null
                  ) {
                    // Find which spot tenant is in
                    const spotId = findSpotForPosition(
                      tenant.position,
                      ramp.shapes,
                      ramp.depth
                    );

                    // Look up spot name from shapes (name stored in .text property)
                    const spot = ramp.shapes.find((s) => s.id === spotId);
                    const spotName = spot?.text || t('screens.ramp.lastMinuteInsertion.dialog.aircraftStatus.spotUnknown');
                    statusLabel = spotName;

                    statusColor = "info";
                    statusVariant = "outlined";
                    statusIcon = <LocationOnIcon />;
                  }

                  return (
                    <li {...props} key={tenant.id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {tenant.tail_number} -{" "}
                            {tenant.aircraft?.model || t('screens.ramp.lastMinuteInsertion.dialog.aircraftStatus.unknown')}
                          </Typography>
                        </Box>
                        <Chip
                          label={statusLabel}
                          size="small"
                          color={statusColor}
                          variant={statusVariant}
                          icon={statusIcon}
                        />
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t('screens.ramp.lastMinuteInsertion.dialog.placeholders.selectAircraft')} />
                )}
                fullWidth
              />
            </Box>

            {/* Step 2: Select Times */}
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('screens.ramp.lastMinuteInsertion.dialog.labels.arrivalTime')}
                </Typography>
                <DateTimePicker
                  value={startTime}
                  onChange={(newValue) => newValue && setStartTime(newValue)}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('screens.ramp.lastMinuteInsertion.dialog.labels.departureTime')}
                </Typography>
                <DateTimePicker
                  value={endTime}
                  onChange={(newValue) => newValue && setEndTime(newValue)}
                  ampm={false}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
            </Stack>

            {/* Step 3: Max Disruption */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('screens.ramp.lastMinuteInsertion.dialog.labels.maxAircraftMoves', { count: maxDisruption })}
              </Typography>
              <Slider
                value={maxDisruption}
                onChange={(_, val) => setMaxDisruption(val as number)}
                min={0}
                max={5}
                step={1}
                marks={[
                  { value: 0, label: t('screens.ramp.lastMinuteInsertion.dialog.slider.marks.zero') },
                  { value: 2, label: t('screens.ramp.lastMinuteInsertion.dialog.slider.marks.two') },
                  { value: 5, label: t('screens.ramp.lastMinuteInsertion.dialog.slider.marks.max') },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Preview Button */}
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={!selectedTenant}
              fullWidth
            >
              {t('screens.ramp.lastMinuteInsertion.dialog.buttons.checkAvailability')}
            </Button>

            {/* Step 4: Preview Results */}
            {previewResult && (
              <Box>
                {previewResult.insertionAnalysis.tier === "zero-disruption" &&
                  (() => {
                    // Extract spot name from placement result
                    const placement =
                      previewResult.insertionAnalysis.placement?.[
                        selectedTenant?.id || ""
                      ];
                    const spotId =
                      placement && "spotId" in placement
                        ? placement.spotId
                        : null;
                    const spot = ramp.shapes.find((s) => s.id === spotId);
                    const spotName = spot?.text || "Spot Unknown";

                    return (
                      <Alert severity="success">
                        <Typography variant="body2" fontWeight={500}>
                          {t('screens.ramp.lastMinuteInsertion.dialog.results.zeroDisruption.title', { spotName })}
                        </Typography>
                        <Typography variant="caption">
                          {t('screens.ramp.lastMinuteInsertion.dialog.results.zeroDisruption.message', { tailNumber: selectedTenant?.tail_number })}
                        </Typography>
                      </Alert>
                    );
                  })()}

                {previewResult.insertionAnalysis.tier === "local-repair" && (
                  <Alert severity="warning">
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      {t('screens.ramp.lastMinuteInsertion.dialog.results.localRepair.title', { count: previewResult.insertionAnalysis.totalMoves })}
                    </Typography>
                    <List dense sx={{ mt: 1 }}>
                      {previewResult.insertionAnalysis.disruptedAircraft.map(
                        (id) => (
                          <ListItem key={id} sx={{ py: 0 }}>
                            <ListItemText
                              primary={getTailNumber(id)}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        )
                      )}
                    </List>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {t('screens.ramp.lastMinuteInsertion.dialog.results.localRepair.message', { tailNumber: selectedTenant?.tail_number })}
                    </Typography>
                  </Alert>
                )}

                {previewResult.insertionAnalysis.tier === "failed" && (
                  <Alert severity="error">
                    <Typography variant="body2" fontWeight={500} gutterBottom>
                      {t('screens.ramp.lastMinuteInsertion.dialog.results.failed.title', { count: maxDisruption })}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {previewResult.insertionAnalysis.analysis.failureReason}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {t('screens.ramp.lastMinuteInsertion.dialog.results.failed.suggestions')}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('screens.ramp.lastMinuteInsertion.dialog.buttons.close')}</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
